import { z } from 'zod'
import { booleanoDeQuery, paginacaoSchema, textoObrigatorio } from './comum'

/**
 * Contatos (seção 3) e o card único do funil (4.3).
 *
 * Sobre telefone: o que sai da API já vem tratado pelo servidor — inteiro para
 * quem tem `ver_telefone`, mascarado para quem não tem (4.8). O DTO nunca
 * carrega o número cru, e por isso `telefoneMascarado` existe: é como a
 * interface sabe que não deve oferecer "copiar número".
 */

export const criarContatoSchema = z.object({
  nome: textoObrigatorio('nome', 160),
  telefonePrincipal: z.string().trim().max(30).optional().nullable(),
  documento: z.string().trim().max(20).optional().nullable(),
  campos: z.record(z.string(), z.unknown()).default({}),
  vendedorId: z.string().uuid().optional().nullable(),
  tagIds: z.array(z.string().uuid()).default([]),
  /**
   * Abre a primeira negociação junto com o contato, quando informado.
   *
   * Funil, etapa e valor não são mais colunas do contato — quem os carrega é
   * a negociação. Manter o atalho aqui evita dois passos no "Novo lead", que
   * é como o cadastro nasce na prática.
   */
  negociacao: z
    .object({
      funilId: z.string().uuid(),
      etapaId: z.string().uuid().optional().nullable(),
      titulo: z.string().trim().max(160).optional().nullable(),
      valor: z.number().min(0).max(9_999_999_999).optional().nullable(),
    })
    .optional()
    .nullable(),
})
export type CriarContatoEntrada = z.infer<typeof criarContatoSchema>

export const atualizarContatoSchema = criarContatoSchema.partial()
export type AtualizarContatoEntrada = z.infer<typeof atualizarContatoSchema>

export const definirTagsSchema = z.object({
  tagIds: z.array(z.string().uuid()),
})

const camposDoFiltro = paginacaoSchema.extend({
  busca: z.string().trim().max(120).optional(),
  vendedorId: z.string().uuid().optional(),
  /** Só os contatos de uma empresa. */
  empresaId: z.string().uuid().optional(),
  semVendedor: booleanoDeQuery(false),
  /**
   * Só quem tem, ou já teve, alguma negociação.
   *
   * A tela de Leads pede isso: contato que chegou pelo WhatsApp e nunca entrou
   * num funil é da base, não é lead. Sem o recorte, a lista virava a agenda
   * inteira do celular e a tela deixava de responder "com quem eu estou
   * negociando". Fica como filtro, e não como regra da rota, porque a mesma
   * consulta serve a exportação e à tela de listas, que precisam da base toda.
   */
  comNegociacao: booleanoDeQuery(false),
  tagIds: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => (v === undefined ? undefined : Array.isArray(v) ? v : v.split(','))),
  /** Filtro por campo customizado, montado a partir de `campos[chave]=valor`. */
  campos: z.record(z.string(), z.string()).optional(),
  ordenar: z.enum(['recentes', 'nome']).default('recentes'),
})

/**
 * O parser de querystring do Fastify é plano: `campos[segmento]=X` chega como
 * a chave literal `"campos[segmento]"`, e não como objeto aninhado. Sem juntar
 * isso aqui, o filtro por campo customizado é descartado em silêncio — que é
 * pior do que dar erro, porque a tela mostra a base inteira como se o filtro
 * tivesse sido aplicado.
 *
 * Vale para o filtro de negociações também (o painel Filtros de Leads é o mesmo de Contatos).
 */
export function juntarCamposDaQuery(bruto: unknown): unknown {
  if (!bruto || typeof bruto !== 'object') return bruto

  const entrada = bruto as Record<string, unknown>
  const campos: Record<string, string> = {
    ...((entrada.campos as Record<string, string> | undefined) ?? {}),
  }

  for (const [chave, valor] of Object.entries(entrada)) {
    const casa = /^campos\[(.+)\]$/.exec(chave)
    if (casa?.[1] && typeof valor === 'string') campos[casa[1]] = valor
  }

  return Object.keys(campos).length > 0 ? { ...entrada, campos } : entrada
}

export const filtroContatosSchema = z.preprocess(juntarCamposDaQuery, camposDoFiltro)

export type FiltroContatos = z.infer<typeof filtroContatosSchema>

/**
 * "Marcar tudo" na lista de contatos: todos os que entram no filtro, não só os
 * cinquenta da página. Vem só o id e as etiquetas de cada um, o que as ações
 * em massa precisam (as etiquetas decidem se "Etiquetar" põe ou tira).
 */
export interface SelecaoDeContatos {
  itens: { id: string; tagIds: string[] }[]
  /** Passou do teto: vieram só os primeiros `LIMITE_DA_SELECAO_DE_CONTATOS`. */
  limitada: boolean
}

/** Teto do "Marcar tudo". Folga grande sobre a base esperada (15 mil). */
export const LIMITE_DA_SELECAO_DE_CONTATOS = 20_000

export interface ContatoTagDto {
  id: string
  nome: string
  cor: string
}

export interface ContatoDuplicadoDto {
  id: string
  nome: string
  documento: string | null
}

export interface ContatoDto {
  id: string
  nome: string
  /** Como o WhatsApp informou. `null` em contato criado à mão. */
  nomeOriginal: string | null
  /** Já formatado ou já mascarado pelo servidor. Nunca o número cru. */
  telefonePrincipal: string | null
  telefoneMascarado: boolean
  documento: string | null
  campos: Record<string, unknown>
  vendedorId: string | null
  vendedorNome: string | null
  /** A empresa em que a pessoa trabalha, quando ligada a uma. */
  empresaId: string | null
  empresaNome: string | null
  naCarteira: boolean
  /** Quantos ciclos de venda este cliente já teve, abertos ou fechados. */
  totalNegociacoes: number
  negociacoesAbertas: number
  /** Soma dos ganhos, para a aba Histórico abrir com o número que importa. */
  totalGanho: number
  tags: ContatoTagDto[]
  /** Outros registros com o mesmo CNPJ ou CPF (4.9). */
  duplicados: ContatoDuplicadoDto[]
  criadoEm: string
  atualizadoEm: string
}
